import {
  Component,
  Input,
  Output,
  EventEmitter,
  signal,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { DataScopeLevel } from '../../../models/gars-permissions.model';
import { COMPANY_CODE_ALIASES, isCompanyCodeMatch } from '../../../services/company-references.store';

export interface ScopeCompanyNode {
  code: string;
  name: string;
  display: string;
  visible?: boolean;
}

export interface ScopeCountryNode {
  country: string;
  companies: string[];
  visible?: boolean;
}

export interface ScopeGroupNode {
  region: string;
  label: string;
  countryCount: number;
  companyCount: number;
  countries: ScopeCountryNode[];
  visible?: boolean;
}

@Component({
  selector: 'app-scope-hierarchy-tree',
  templateUrl: './scope-hierarchy-tree.component.html',
  styleUrls: ['./scope-hierarchy-tree.component.scss'],
  standalone: false,
})
export class ScopeHierarchyTreeComponent implements OnChanges {
  @Input() selectionLevel: DataScopeLevel = 'company';
  @Input() groups: ScopeGroupNode[] = [];
  @Input() searchQuery = '';
  @Input() selectedRegions: string[] = [];
  @Input() selectedCountries: string[] = [];
  @Input() selectedCompanies: string[] = [];
  @Input() activeCompanyCodes: Set<string> = new Set();

  /** Custom company display formatter (e.g., "Company Name (CODE)") */
  @Input() formatCompanyDisplay: (comp: string) => string = comp => comp;

  /** Region company count resolver (0 for empty regions) */
  @Input() getRegionCompanyCount: (region: string) => number = () => 0;

  /** Country company codes resolver */
  @Input() getCompanyCodesForCountry: (country: string, region?: string) => string[] = () => [];

  /** Resolve canonical company code (e.g., "Cement Australia" -> "QCL") */
  @Input() resolveCanonicalCompanyCode: (comp: string) => string = comp => comp;

  /** Custom check for whether a company is active in current scope */
  @Input() isCompanyActive: ((comp: string) => boolean) | null = null;

  @Output() regionToggle = new EventEmitter<string>();
  @Output() countryToggle = new EventEmitter<{ country: string; region: string }>();
  @Output() companyToggle = new EventEmitter<string>();

  // Expand / collapse sets
  readonly collapsedRegions = signal<Set<string>>(new Set());
  readonly collapsedCountries = signal<Set<string>>(new Set());

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['searchQuery'] && this.searchQuery.trim()) {
      // Auto-expand all matching branches when searching
      this.collapsedRegions.set(new Set());
      this.collapsedCountries.set(new Set());
    }
  }

  isRegionCollapsed(region: string): boolean {
    return this.collapsedRegions().has(region.toLowerCase());
  }

  toggleRegionCollapse(region: string, event?: Event): void {
    event?.stopPropagation();
    const next = new Set(this.collapsedRegions());
    const key = region.toLowerCase();
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    this.collapsedRegions.set(next);
  }

  isCountryCollapsed(region: string, country: string): boolean {
    return this.collapsedCountries().has(`${region.toLowerCase()}::${country.toLowerCase()}`);
  }

  toggleCountryCollapse(region: string, country: string, event?: Event): void {
    event?.stopPropagation();
    const next = new Set(this.collapsedCountries());
    const key = `${region.toLowerCase()}::${country.toLowerCase()}`;
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    this.collapsedCountries.set(next);
  }

  // ── Selection State Queries ──

  isRegionSelected(group: ScopeGroupNode): boolean {
    if (group.companyCount === 0) return false;
    if (this.selectionLevel === 'region') {
      const regLower = group.region.toLowerCase().trim();
      return this.selectedRegions.some(r => r.toLowerCase().trim() === regLower);
    }
    // In Country or Company mode: active if and only if all constituent countries with companies are selected
    const validCountries = group.countries.filter(c => c.companies.length > 0);
    if (!validCountries.length) return false;
    return validCountries.every(c => this.isCountrySelected(c, group));
  }

  isRegionIndeterminate(group: ScopeGroupNode): boolean {
    if (group.companyCount === 0 || this.isRegionSelected(group)) return false;
    const validCountries = group.countries.filter(c => c.companies.length > 0);
    return validCountries.some(c => this.isCountrySelected(c, group) || this.isCountryIndeterminate(c, group));
  }

  isCountrySelected(c: ScopeCountryNode, group: ScopeGroupNode): boolean {
    if (c.companies.length === 0) return false;
    if (this.selectionLevel === 'region') {
      return this.isRegionSelected(group);
    }
    if (this.selectionLevel === 'country') {
      const cLower = c.country.toLowerCase().trim();
      return this.selectedCountries.some(item => item.toLowerCase().trim() === cLower);
    }
    // Company level: selected IF AND ONLY IF every company in this country is selected
    return c.companies.every(comp => this.isCompanySelected(comp, c, group));
  }

  isCountryIndeterminate(c: ScopeCountryNode, group: ScopeGroupNode): boolean {
    if (c.companies.length === 0 || this.isCountrySelected(c, group)) return false;
    if (this.selectionLevel === 'company') {
      return c.companies.some(comp => this.isCompanySelected(comp, c, group));
    }
    return false;
  }

  isCompanySelected(comp: string, c?: ScopeCountryNode, group?: ScopeGroupNode): boolean {
    if (this.isCompanyActive && this.isCompanyActive(comp)) {
      return true;
    }
    const compUpper = comp.trim().toUpperCase();
    if (this.activeCompanyCodes.has(compUpper)) return true;
    if (COMPANY_CODE_ALIASES[compUpper] && this.activeCompanyCodes.has(COMPANY_CODE_ALIASES[compUpper])) return true;

    const canon = this.resolveCanonicalCompanyCode(comp);
    if (canon && this.activeCompanyCodes.has(canon.trim().toUpperCase())) return true;
    if (canon && COMPANY_CODE_ALIASES[canon.trim().toUpperCase()] && this.activeCompanyCodes.has(COMPANY_CODE_ALIASES[canon.trim().toUpperCase()])) return true;

    // Check if any company code for this country matches activeCompanyCodes
    if (c) {
      const cCodes = this.getCompanyCodesForCountry(c.country, group?.region);
      for (const code of cCodes) {
        const cU = code.trim().toUpperCase();
        const codeActive = this.activeCompanyCodes.has(cU) || (!!COMPANY_CODE_ALIASES[cU] && this.activeCompanyCodes.has(COMPANY_CODE_ALIASES[cU]));
        if (codeActive) {
          const cCanon = this.resolveCanonicalCompanyCode(code);
          const compFormatted = this.formatCompanyDisplay(comp).toLowerCase();
          if (
            compFormatted.includes(`(${cU.toLowerCase()})`) ||
            (cCanon && compFormatted.includes(`(${cCanon.toLowerCase()})`)) ||
            isCompanyCodeMatch(compUpper, cU) ||
            (canon && isCompanyCodeMatch(canon, cU))
          ) {
            return true;
          }
        }
      }
    }

    const compLower = comp.trim().toLowerCase();
    const compFormatted = this.formatCompanyDisplay(comp).toLowerCase().trim();
    const canonLower = canon ? canon.trim().toLowerCase() : '';

    return this.selectedCompanies.some(item => {
      const itemClean = item.trim();
      const itemLower = itemClean.toLowerCase();
      const itemUpper = itemClean.toUpperCase();
      const itemCanon = this.resolveCanonicalCompanyCode(itemClean);
      const itemFormatted = this.formatCompanyDisplay(itemClean).toLowerCase().trim();
      return (
        itemLower === compLower ||
        itemFormatted === compFormatted ||
        itemUpper === compUpper ||
        isCompanyCodeMatch(compUpper, itemUpper) ||
        (canon && (itemLower === canonLower || isCompanyCodeMatch(canon, itemUpper) || (itemCanon && isCompanyCodeMatch(canon, itemCanon))))
      );
    });
  }

  isCompanyActiveInTree(comp: string, c: ScopeCountryNode, group: ScopeGroupNode): boolean {
    return this.isCompanySelected(comp, c, group) || this.isCompanyInherited(c, group);
  }

  isCompanyInherited(c: ScopeCountryNode, group: ScopeGroupNode): boolean {
    if (this.selectionLevel === 'region') {
      return this.isRegionSelected(group);
    }
    if (this.selectionLevel === 'country') {
      return this.isCountrySelected(c, group);
    }
    return false;
  }

  getEmptyRegionMessage(group: ScopeGroupNode): string {
    return `${group.label} does not have any companies`;
  }

  // ── Actions ──

  onRegionHeadClick(group: ScopeGroupNode, event: Event): void {
    this.toggleRegionCollapse(group.region, event);
  }

  onRegionSelectClick(group: ScopeGroupNode, event: Event): void {
    event.stopPropagation();
    if (group.companyCount === 0) return;
    this.regionToggle.emit(group.region);
  }

  onCountryHeadClick(c: ScopeCountryNode, group: ScopeGroupNode, event: Event): void {
    this.toggleCountryCollapse(group.region, c.country, event);
  }

  onCountrySelectClick(c: ScopeCountryNode, group: ScopeGroupNode, event: Event): void {
    event.stopPropagation();
    if (c.companies.length === 0) return;
    this.countryToggle.emit({ country: c.country, region: group.region });
  }

  onCompanyClick(comp: string): void {
    if (this.selectionLevel === 'company') {
      this.companyToggle.emit(comp);
    }
  }
}
